import { useEffect, useRef, useState } from "react";
import { Ban, Flag, Loader2, Paperclip, Pin, Reply, Send, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Chip } from "@/components/community/kit";
import { ROLE_TONE, ago } from "@/lib/community";

const initials = (name) =>
  (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const Attachment = ({ file }) => {
  const image = (file.type || "").startsWith("image/");
  return image ? (
    <a href={file.url} target="_blank" rel="noreferrer" data-testid="chat-attachment-image">
      <img src={file.url} alt={file.name} loading="lazy"
        className="mt-2 max-h-56 rounded-lg border border-ink-600 object-cover" />
    </a>
  ) : (
    <a href={file.url} target="_blank" rel="noreferrer" data-testid="chat-attachment-file"
      className="mt-2 inline-flex items-center gap-2 rounded-lg border border-ink-600 bg-ink-900 px-3 py-2 text-xs text-slate-300 hover:border-gold/40">
      <Paperclip className="h-3.5 w-3.5" /> {file.name || "Attachment"}
    </a>
  );
};

export const Message = ({ message, communityId, channelId, mine, canModerate, canPost, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const loaded = useRef(false);

  const loadReplies = async () => {
    const { data } = await api.get(
      `/communities/${communityId}/channels/${channelId}/messages`,
      { params: { parent: message.id, limit: 100 } });
    setReplies(data.messages);
    loaded.current = true;
  };

  useEffect(() => {
    if (open && !loaded.current) loadReplies().catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const act = async (fn, key) => {
    setBusy(key); setError("");
    try { await fn(); } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const sendReply = () => act(async () => {
    await api.post(`/communities/${communityId}/channels/${channelId}/messages`,
      { body: reply, parent_id: message.id });
    setReply("");
    await loadReplies();
    onChanged?.();
  }, "reply");

  return (
    <li data-testid={`chat-message-${message.id}`} className="group flex gap-3 py-3">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-mono text-[11px] ${
        ROLE_TONE[message.role] || ROLE_TONE.member}`}>
        {initials(message.name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-100">{message.name || "Member"}</p>
          {message.role && message.role !== "member" && (
            <Chip className={ROLE_TONE[message.role]}>{message.role}</Chip>
          )}
          <span className="font-mono text-[10px] text-slate-600">{ago(message.at)}</span>
          {message.pinned && <Pin className="h-3 w-3 text-gold" />}
        </div>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-300">
          {message.body}
        </p>
        {(message.attachments || []).map((f, i) => <Attachment key={i} file={f} />)}

        <div className="mt-1.5 flex flex-wrap items-center gap-3 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
          <button data-testid={`chat-reply-${message.id}`} onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-gold">
            <Reply className="h-3 w-3" /> {message.replies > 0 ? `${message.replies} replies` : "Reply"}
          </button>
          {canModerate && (
            <button data-testid={`chat-pin-${message.id}`}
              onClick={() => act(async () => {
                await api.post(`/communities/${communityId}/messages/${message.id}/pin`, null,
                  { params: { on: !message.pinned } });
                onChanged?.();
              }, "pin")}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-gold">
              <Pin className="h-3 w-3" /> {message.pinned ? "Unpin" : "Pin"}
            </button>
          )}
          {(mine || canModerate) && (
            <button data-testid={`chat-delete-${message.id}`}
              onClick={() => act(async () => {
                await api.delete(`/communities/${communityId}/messages/${message.id}`);
                onChanged?.();
              }, "delete")}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-red-300">
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          )}
          {!mine && (
            <>
              <button data-testid={`chat-report-${message.id}`}
                onClick={() => act(async () => {
                  const reason = window.prompt("What is wrong with this message?");
                  if (!reason) return;
                  await api.post(`/communities/${communityId}/messages/${message.id}/report`, { reason });
                }, "report")}
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-amber-300">
                <Flag className="h-3 w-3" /> Report
              </button>
              <button data-testid={`chat-block-${message.id}`}
                onClick={() => act(async () => {
                  await api.post(`/communities/${communityId}/block`,
                    { user_id: message.user_id, on: true });
                  onChanged?.();
                }, "block")}
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-slate-500 hover:text-red-300">
                <Ban className="h-3 w-3" /> Hide them
              </button>
            </>
          )}
          {busy && <Loader2 className="h-3 w-3 animate-spin text-gold" />}
        </div>

        {error && <p data-testid="chat-message-error" className="mt-1 text-xs text-red-300">{error}</p>}

        {open && (
          <div data-testid={`chat-thread-${message.id}`} className="mt-3 space-y-3 border-l-2 border-ink-600 pl-4">
            {replies.length === 0 && <p className="text-xs text-slate-600">No replies yet.</p>}
            {replies.map((r) => (
              <div key={r.id} data-testid={`chat-reply-row-${r.id}`}>
                <p className="text-xs text-slate-400">
                  <span className="text-slate-200">{r.name}</span> · {ago(r.at)}
                </p>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-300">{r.body}</p>
              </div>
            ))}
            {canPost && (
              <div className="flex gap-2">
                <input data-testid={`chat-reply-input-${message.id}`} value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && reply.trim()) sendReply(); }}
                  placeholder="Reply in this thread"
                  className="h-9 min-w-0 flex-1 rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-200 placeholder:text-slate-600" />
                <button data-testid={`chat-reply-send-${message.id}`} onClick={sendReply}
                  disabled={!reply.trim() || busy === "reply"}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-gold/30 bg-gold/10 text-gold disabled:opacity-40">
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
};
